import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import * as Sentry from '@sentry/react';

import { getOneProduct } from 'api/product';
import { getProductList } from 'api/trade_in';

import ProductScreen from 'screens/Product/ProductScreen';
import { DA_CATEGORIES } from '../../config/constants';

const ProductContainer = ({
    token,
    isAuthed,
    singpassStatus
}) => {
    const useQueryParams = () => {
        const location = useLocation();

        return new URLSearchParams(location.search);
    };

    const { slug } = useParams();
    const queryParams = useQueryParams();
    const rentalTerm = queryParams.get('rental_term');
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState({});
    const [colors, setColors] = useState([]);
    const [colorsGallery, setColorsGallery] = useState([]);
    const [selectedRentalPeriod, setSelectedRentalPeriod] = useState({ id: -1 });
    const [isOpen, setIsOpen] = useState(false);
    const [attribute, setAttribute] = useState([]);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedStorage, setSelectedStorage] = useState(null);
    const [images, setImages] = useState(null);
    const [maxTradeInValue, setMaxTradeInValue] = useState(925);
    const [isProductDA, setIsProductDA] = useState(false);

    function getAttributeLabel(product, value) {
        if (!DA_CATEGORIES.includes(product?.category?.name)) return value;

        switch (value) {
            case '4GB':
                return 'SC+';
            case '8GB':
                return 'SC+ Premium';
            default:
                return value;
        }
    }

    const getProduct = async () => {
        setLoading(true);

        try {
            const oneProduct = await getOneProduct(slug);

            await getProductsList();

            window.document.title = oneProduct?.data?.name || 'Samsung | Cinch';

            setProduct(oneProduct);
            setIsProductDA(DA_CATEGORIES.includes(oneProduct?.data?.category?.name));

            if (oneProduct.data.attributes.storage && oneProduct.data.attributes.storage.values !== 0) {
                const tmp = [];

                oneProduct.data.attributes.storage.values.map(item => tmp.push({
                    value: item.id,
                    label: getAttributeLabel(oneProduct.data, item.value),
                    item,
                    stock: (item.stock === undefined || item.stock === 0) ? 0 : 1,
                    is_out_of_stock: isStorageInStock(oneProduct.data.internal_asset_codes, oneProduct.data.colors, item.id)
                }));

                setAttribute(tmp);

                if (oneProduct?.data?.slug === 'samsung-galaxy-s25-edge') {
                    const storage = oneProduct.data.attributes.storage.values.find(item => item.value === '512GB');

                    setSelectedStorage({
                        value: storage.id,
                        label: getAttributeLabel(oneProduct.data, storage.value),
                        item: storage
                    });
                } else {
                    setSelectedStorage({
                        value: oneProduct.data.attributes.storage.values[0].id,
                        label: oneProduct.data.attributes.storage.values[0].value,
                        item: oneProduct.data.attributes.storage.values[0]
                    });
                }
            } else {
                setAttribute(oneProduct.data.attributes);
            }

            let colorIndex = 0;

            const medias = oneProduct.data.medias;
            const sortedMedia = [];

            if (oneProduct.data.colors !== 0) {
                setColorsGallery(oneProduct.data.colors);

                const tmp = [];

                for (const color of oneProduct.data.colors) {
                    tmp.push({
                        value: color.id,
                        label: <div className='selectOption'>
                            <span className='selectOption__color' style={{ background: color.hex_code || color.name }} />
                            <span className='selectOption__title'>{color.name}</span>
                        </div>,
                        text: color.name,
                        index: colorIndex
                    });

                    if (medias && medias.length !== 0) {
                        const indexPrimary = medias.findIndex(e => e.color_id === color.id && e.primary);

                        if (indexPrimary > -1) {
                            const item = medias.splice(indexPrimary, 1)[0];
                            const specificColor = { id: color.id, images: [{ ...item }] };

                            sortedMedia.push(specificColor);

                            colorIndex++;

                            for (let index = 0; index < medias.length; index++) {
                                const element = medias[index];

                                if (element.color_id === color.id) {
                                    sortedMedia[sortedMedia.length - 1].images.push({ ...element });

                                    colorIndex++;
                                }
                            }
                        } else {
                            const specificColor = { id: color.id, images: [] };

                            sortedMedia.push(specificColor);

                            for (let index = 0; index < medias.length; index++) {
                                const element = medias[index];

                                if (element.color_id === color.id) {
                                    sortedMedia[sortedMedia.length - 1].images.push({ ...element });

                                    colorIndex++;
                                }
                            }
                        }
                    }
                }

                setColors(tmp);
                setSelectedColor(tmp[0]);
            }

            setImages(sortedMedia);

            const validRentalTerm = oneProduct.data.rental_period.find(e => e.duration_time === Number(rentalTerm));

            setSelectedRentalPeriod(validRentalTerm || oneProduct.data.rental_period[oneProduct.data.rental_period.length - 1]);
        } catch (error) {
            Sentry.captureException(error);
        }

        setLoading(false);
    };

    const getProductsList = async () => {
        await getProductList().then(response => {
            if (response.data && response.data.length > 0) {
                const maxPrice = Math.max(...response.data.map((product) => product.GradeAPrice));

                setMaxTradeInValue(maxPrice);
            }
        });
    };

    const isStorageInStock = (iacs, colors, attributeId) => {
        const availableVariants = iacs.filter(e => e.product_attribute_value_id === attributeId);
        const stocks = [];

        colors.forEach(color => {
            const iac = availableVariants.find(e => e.color_id === color.id);

            if (iac?.in_stock !== 1) stocks.push(iac?.id);
        });

        return stocks.length === colors.length;
    };

    useEffect(() => {
        getProduct();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthed, slug, rentalTerm]);

    return (
        <>
            <Helmet>
                <title>{`${product.data?.name || 'Samsung | Cinch'}`}</title>
                <meta name='description' content={`${product.data?.description}`} />
                <link rel='canonical' href={`https://samsung.cinch.sg/product/${product.data?.slug}`} />
            </Helmet>
            <ProductScreen
                images={images}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                selectedStorage={selectedStorage}
                setSelectedStorage={setSelectedStorage}
                attribute={attribute}
                setAttribute={setAttribute}
                singpassStatus={singpassStatus}
                messageType=''
                setIsOpen={setIsOpen}
                isOpen={isOpen}
                selectedRentalPeriod={selectedRentalPeriod}
                setSelectedRentalPeriod={setSelectedRentalPeriod}
                token={token}
                colors={colors}
                setColors={setColors}
                colorsGallery={colorsGallery}
                isOrdered={false}
                order={null}
                product={product.data || {}}
                loading={loading}
                isAuthed={isAuthed}
                maxTradeInValue={maxTradeInValue}
                isProductDA={isProductDA}/>
        </>
    );
};

ProductContainer.propTypes = {
    token: PropTypes.string.isRequired,
    isAuthed: PropTypes.bool,
    singpassStatus: PropTypes.string,
    cart: PropTypes.any
};

const mapStateToProps = ({ auth, cart }) => {
    const { token, isAuthed, profile } = auth;

    return {
        token,
        isAuthed,
        singpassStatus: profile.singpass_status,
        cart
    };
};

export default connect(mapStateToProps, null)(ProductContainer);
